import parsePhoneNumberFromString, {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { ChangeEvent, FC, useEffect, useState } from "react";
import Select from "react-select";
import { useI18n } from "../../hooks/useTranslations";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Input from "./Input";
import styles from "./PhoneNumber.module.css";

type PhoneNumberInputProps = {
  labelId: string;
  isDisabled?: boolean;
  isLoading?: boolean;

  phoneNumber?: string | null;

  onSetPhoneNumber: (value: string | null) => void;
  onSetCountryPhone: (value: CountryCode | null) => void;
  onError: (value?: string | null) => void;
};

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
  labelId,
  isDisabled,
  isLoading,
  phoneNumber,
  onSetPhoneNumber,
  onSetCountryPhone,
  onError,
}) => {
  const i18n = useI18n();

  const countries = getCountries();
  const countryOptions = countries.map((c) => ({
    value: c,
    label: `${c} (+${getCountryCallingCode(c)})`,
  }));

  const [state, setState] = useState<{
    number?: string;
    isNumberValid: boolean;
    country?: CountryCode;
  }>({
    isNumberValid: true,
  });

  useEffect(() => {
    if (phoneNumber) {
      const parsed = parsePhoneNumberFromString(phoneNumber);
      const formattedNumber = parsed?.isValid() ? parsed.formatNational() : undefined;
      const countryCode = parsed?.isValid() ? countries.find((x) => x == parsed?.country) : undefined;

      setState({
        country: countryCode,
        isNumberValid: !countryCode || !!parsed?.isValid(),
        number: formattedNumber ?? phoneNumber,
      });
    }
  }, [phoneNumber]);

  const validate = (number: string | undefined, country: CountryCode | undefined) => {
    if (!number) {
      setState({
        number: number,
        country: country,
        isNumberValid: !country,
      });
      onSetCountryPhone(null);
      onSetPhoneNumber(null);
      onError(country ? i18n.t.common.enterAPhoneNumber : null);

      return;
    }

    if (number) {
      if (!country) {
        setState({
          number: number,
          isNumberValid: false,
        });

        onError(i18n.t.common.selectACountry);
      } else {
        const formatted = new AsYouType(country).input(number);
        const parsed = parsePhoneNumberFromString(number, country);

        const valid = parsed?.isValid();
        setState({
          number: formatted,
          isNumberValid: !!valid,
          country: country,
        });

        if (parsed && valid) {
          onSetPhoneNumber(parsed.number);
          onSetCountryPhone(country);

          onError(null);
        } else {
          onError(i18n.t.common.phoneNumberIsInvalid);
        }
      }
    }
  };

  useEffect(() => {
    onSetCountryPhone(state?.country ?? null);
  }, [state?.country]);

  return (
    <div className={styles.field}>
      <div className={styles.country}>
        <Select
          id={`${labelId}_country`}
          options={countryOptions}
          value={countryOptions.find((x) => x.value === state?.country) ?? null}
          isLoading={isLoading}
          isDisabled={isDisabled || isLoading}
          placeholder={`${i18n.t.common.country}...`}
          getOptionValue={(x) => x.value}
          getOptionLabel={(x) => x.label}
          onChange={(value) => validate(state.number, value?.value)}
          isClearable
          styles={reactSelectStyles<{ value: CountryCode; label: string }>(!state?.country && !!state?.number)}
          menuPosition="fixed"
        />
      </div>

      <Input
        id={`${labelId}_phone`}
        disabled={isDisabled || isLoading}
        onChange={(e: ChangeEvent<HTMLInputElement>) => validate(e.target.value, state.country)}
        value={state?.number ?? ""}
        isInvalid={!!state.country && !state.number}
      />
    </div>
  );
};

export default PhoneNumberInput;
